import { useState, useRef, useEffect, useCallback, useMemo, useId } from "react";
import { X, Plus, Loader2, ArrowDown } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { CoachPill } from "./coach/CoachPill";
import { HIT_AREA_FROM_36_LEFTWARD, HIT_AREA_TALLER } from "./coach/hitArea";
import { Composer } from "./coach/Composer";
import { MessageBubble } from "./coach/MessageBubble";
import { useCoachSession } from "./coach/useCoachSession";
import { useCoachStream } from "./coach/useCoachStream";
import { budgetSpentText, useRenewalTimer, type BudgetSpent } from "./coach/budgetSpent";
import { SettingsPrivacyLink } from "./consent/SettingsPrivacyLink";
import { COPY } from "../copy/firstRun";
import { track } from "../analytics";
import { AMBER, AMBER_BORDER, CREAM, CREAM_DIM, CREAM_SOFT, TERRACOTTA, TERRACOTTA_SOFT, WARM_DARK } from "./coach/tokens";
import { TOKENS } from "./ui/dashboard-primitives";
import type { AICoachProps, Message } from "./coach/types";

/**
 * The coach page (Coach v3). One render mode: it exists only while /coach is
 * mounted, so there is no `isOpen`, no backdrop, no slide and no dialog role.
 * Identity is carried by the per-message avatars rather than an identity
 * header — the page's own chrome is a single eyebrow-plus-controls row, the
 * same vocabulary every other page uses.
 *
 * The chrome is English-only (design decision D9). German survives as coach
 * *content* from the backend, not as UI copy.
 */
export function AICoach({
  onClose,
  initialMessage = null,
  onInitialMessageSent,
  aiOff = false,
  onConsentRefused,
}: AICoachProps) {
  const [input, setInput] = useState("");
  // The server refused a turn for want of the AI consent (a 403 with its marker, CS-D19): the consent
  // was withdrawn elsewhere, so the page shuts as if `me` had said so, whatever `me` still says, and
  // sends nothing more from here. Agreeing in Settings › Privacy and coming back opens it again (CO-D3).
  // Page state only, as the budget's notice is: a reload asks the server again.
  const [consentRefused, setConsentRefused] = useState(false);
  const shut = aiOff || consentRefused;
  const [showScrollDown, setShowScrollDown] = useState(false);
  // A spent AI budget (#394, C27): what the strip above the composer says, and
  // until when. Page state and nothing more: it is never stored, so a reload
  // forgets it and the next send learns it again, and "New" leaves it alone,
  // because a new chat does not give the allowance back. `strip` is the sentence
  // the user was shown when it began, so it does not reword itself ("tomorrow"
  // to "today") as the clock moves.
  const [budgetNotice, setBudgetNotice] = useState<(BudgetSpent & { strip: string }) | null>(
    null,
  );

  const liveRegionId = useId();
  const budgetStripId = useId();
  const consentStripId = useId();

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Whether new content should keep the view pinned to the bottom. Broken by a
  // user scroll-up gesture, re-armed when they return to the bottom. Kept off
  // the render path (a ref) so streaming doesn't thrash.
  const stickToBottomRef = useRef<boolean>(true);
  // Where the reader actually is, by any means of scrolling (wheel, touch, scrollbar,
  // keyboard): set on every scroll event. `stickToBottomRef` is broken by a gesture
  // only, so a reader who dragged the scrollbar up is still "stuck" to it; the resize
  // pin below asks both.
  const atBottomRef = useRef<boolean>(true);
  // The question the last send carried, to give back to an empty composer if that turn
  // is refused for a spent budget (#394): a draft is not lost to a limit.
  const lastSentRef = useRef<string>("");
  // Accumulates streamed chunks silently, off the render path, exactly like
  // the pre-extraction bufferRef did. onDelta must NOT setMessages per chunk
  // — MessageBubble shows only "thinking" dots while pending, so a per-chunk
  // setMessages would re-render (and re-run ReactMarkdown over) the entire
  // transcript for zero visible benefit. Reveal happens once, atomically, in
  // onDone/onError.
  const revealBufferRef = useRef<string>("");
  // The turn the page is on: the id of the assistant message the last send made, or null once "New"
  // has replaced the transcript. A stream outlives its `done` (the cards picked for the reply and the
  // follow-ups come after it), so the person can send the next question while the older stream is
  // still open; its late events are bound to its own turn by this id (#449, P9).
  const latestTurnRef = useRef<string | null>(null);

  // The eyebrow's date. Pinned to en-US because all UI copy is English
  // (CLAUDE.md) — the runtime locale would otherwise reformat it per browser,
  // and this string is asserted in a test. "Thu, Aug 6" per Coach v3 line 62.
  const today = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
    [],
  );

  // ── Session lifecycle ────────────────────────────────────────────────────
  // Declared before useCoachStream (below) on purpose: the two hooks have a
  // circular dependency (useCoachStream's options need setMessages /
  // setFollowups, which this hook owns) and useCoachSession is the side that
  // doesn't need anything back — it never calls `stop()`. See its module doc
  // comment for the full story.
  //
  // Takes no options: the coach is a route, so it is mounted exactly when it
  // is open, and the opener now comes from the backend (which reads
  // coach_language itself), so this no longer waits on a profile query.
  const {
    sessionId,
    setSessionId,
    messages,
    setMessages,
    followups,
    setFollowups,
    isLoadingSession,
    isEphemeral: sessionEphemeral,
    startNewChat,
    dismissOpener,
    answerProposal,
    answerOffer,
  } = useCoachSession({ aiOff: shut });

  // ── Streamed reply reveal ─────────────────────────────────────────────────
  // useCoachStream owns the wire (SSE parsing, abort) but not the reveal
  // buffer — it's ignorant of the message list, so it hands each chunk to
  // onDelta and lets the shell decide what to do with it. revealBufferRef is
  // where those chunks pile up silently until onDone/onError reveals them
  // atomically into the pending assistant turn that sendMessage pushed.

  const scrollAfterReveal = useCallback(() => {
    // Scroll to bottom after the reveal mounts.
    requestAnimationFrame(() => {
      if (!stickToBottomRef.current) return;
      const container = messagesContainerRef.current;
      if (container) container.scrollTop = container.scrollHeight;
    });
  }, []);

  const { isStreaming, send, stop } = useCoachStream({
    onDelta: (chunk) => {
      revealBufferRef.current += chunk;
    },
    onFollowups: (f, turn) => {
      // Suggestions for the next message, shown only while their turn is the latest: a later question
      // (or "New") has replaced what they were written for.
      if (turn === latestTurnRef.current) setFollowups(f);
    },
    onArtifact: (artifact, turn) =>
      setMessages((prev) => {
        // The card goes to the message of the turn that sent it, not to "the last one": a stage-two
        // card comes after `done`, when the next question may already be pending below it. No such
        // message (the transcript was replaced) and the card is dropped.
        const at = prev.findIndex((m) => m.role === "assistant" && m.turn === turn);
        if (at === -1) return prev;
        const target = prev[at];
        const existing = target.artifacts ?? [];
        // `id` is unique per emission (design spec §3.2), so it dedupes a
        // re-delivered line and keys the list.
        if (existing.some((a) => a.id === artifact.id)) return prev;
        const updated = prev.slice();
        updated[at] = { ...target, artifacts: [...existing, artifact] };
        return updated;
      }),
    onAbort: () => {
      // The user stopped the turn (the composer's stop control, or "New" while a reply was
      // streaming) before its `done`. What had streamed in is not final and the server stores none
      // of it, so none of it is shown and the pending bubble goes: the transcript is what a reload
      // would show, the question alone (S24, #449). No line takes its place, so no new copy; the
      // question is stored, so it is not given back to the composer.
      revealBufferRef.current = "";
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        return last && last.role === "assistant" && last.pending ? prev.slice(0, -1) : prev;
      });
    },
    onError: (message) => {
      // Override, not append — mirrors the original's
      // `overrideContent ?? bufferRef.current`: an error discards whatever
      // had streamed in so far.
      revealBufferRef.current = "";
      setMessages((prev) => {
        const updated = prev.slice();
        const last = updated[updated.length - 1];
        if (last && last.role === "assistant" && last.pending) {
          updated[updated.length - 1] = { ...last, pending: false, content: message };
        }
        return updated;
      });
      scrollAfterReveal();
    },
    onBudgetSpent: (spent) => {
      // The coach's own message, in the bubble the pending reply already holds,
      // and not an error (the two differ in tone, not in component): nothing
      // streamed, so there is nothing to discard. The refused turn was never
      // stored by the server, so a reload shows neither this nor the question.
      const { bubble, strip } = budgetSpentText(spent);
      revealBufferRef.current = "";
      setMessages((prev) => {
        const updated = prev.slice();
        const last = updated[updated.length - 1];
        if (last && last.role === "assistant" && last.pending) {
          updated[updated.length - 1] = { ...last, pending: false, content: bubble };
        }
        return updated;
      });
      setBudgetNotice({ ...spent, strip });
      // The refused question goes back into an empty composer. It stays on screen as the
      // user's bubble too, which the server never stored.
      setInput((current) => (current === "" ? lastSentRef.current : current));
      scrollAfterReveal();
    },
    onBusyRefused: () => {
      // The busy line is already in the bubble (`onError`, which ran first). The
      // refused question goes back into an EMPTY composer, by the spent budget's
      // own rule: a draft the user has started since is left alone, and the
      // question stays on screen as their bubble either way (CAP-O42). Nothing
      // locks and nothing is sent again.
      setInput((current) => (current === "" ? lastSentRef.current : current));
    },
    onConsentRefused: () => {
      // The turn's own line is already in the bubble (`onError` ran first). The page shuts and the
      // question goes back into an EMPTY composer by the spent budget's own rule (R10: without the
      // consent a route does what it does for a spent budget): the server stored nothing, and a draft
      // the user has started since is left alone. Nothing is sent again from here.
      setConsentRefused(true);
      setInput((current) => (current === "" ? lastSentRef.current : current));
      onConsentRefused?.();
    },
    onDone: () => {
      // Snapshot before resetting — the setMessages updater below runs
      // during the next render, by which time the ref would already read "".
      const finalContent = revealBufferRef.current;
      revealBufferRef.current = "";
      setMessages((prev) => {
        const updated = prev.slice();
        const last = updated[updated.length - 1];
        if (last && last.role === "assistant" && last.pending) {
          updated[updated.length - 1] = { ...last, pending: false, content: finalContent };
        }
        return updated;
      });
      scrollAfterReveal();
    },
  });

  // The strip takes itself away at the instant it names. A budget that never
  // renews (no instant) stays until a reload.
  const clearBudgetNotice = useCallback(() => setBudgetNotice(null), []);
  useRenewalTimer(budgetNotice?.unlockAt ?? null, clearBudgetNotice);

  // ── Scroll tracking ───────────────────────────────────────────────────────
  //
  // stickToBottomRef is only *broken* by a user gesture (wheel/touch up), never
  // by a programmatic scroll — so auto-scroll can't suppress itself. onScroll
  // only re-arms the stick when the view returns to the bottom.

  const handleScroll = useCallback(() => {
    const c = messagesContainerRef.current;
    if (!c) return;
    const atBottom = c.scrollHeight - c.scrollTop - c.clientHeight < 40;
    atBottomRef.current = atBottom;
    if (atBottom) stickToBottomRef.current = true;
    setShowScrollDown(!atBottom);
  }, []);

  // User dragging/wheeling upward away from the bottom breaks the stick.
  const handleScrollIntent = useCallback((e: React.WheelEvent | React.TouchEvent) => {
    if ("deltaY" in e && e.deltaY >= 0) return; // only an upward gesture breaks it
    const c = messagesContainerRef.current;
    if (!c) return;
    if (c.scrollHeight - c.scrollTop - c.clientHeight > 40) {
      stickToBottomRef.current = false;
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    const c = messagesContainerRef.current;
    if (!c) return;
    c.scrollTo({ top: c.scrollHeight, behavior: "smooth" });
    stickToBottomRef.current = true;
    setShowScrollDown(false);
  }, []);

  // ── Send / stop ───────────────────────────────────────────────────────────
  // The wire handling (fetch, SSE parsing, abort) lives in useCoachStream
  // now. sendMessage keeps only what's message-list state: the empty/
  // in-flight guard, and pushing the user turn + the empty pending assistant
  // turn that the stream's callbacks above fill in and reveal.

  const sendMessage = useCallback(
    // `byPerson` is false for the one message the page sends without the person writing it (the record celebration
    // below): it goes to the coach like any other, and analytics does not count it (ruling R10: "that you messaged
    // the coach" has to stay true).
    async (userContent: string, { byPerson = true }: { byPerson?: boolean } = {}) => {
      // A locked composer sends nothing, from any path: the dock's draft and the
      // log modal's PR message come through here too (#394). So does a shut one: a page that knows the
      // AI consent is missing sends no request the server would refuse (#399).
      if (!userContent.trim() || isStreaming || budgetNotice !== null || shut) return;

      const currentSessionId = sessionId ?? crypto.randomUUID();
      if (!sessionId) setSessionId(currentSessionId);

      const userMsg: Message = { role: "user", content: userContent.trim() };
      lastSentRef.current = userMsg.content;
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setFollowups([]);
      stickToBottomRef.current = true; // sending always re-pins to the bottom
      setShowScrollDown(false);

      // The turn's id rides on its message and comes back with the stream's late events (a stage-two
      // card, the follow-ups), which find the message by it.
      const turn = crypto.randomUUID();
      latestTurnRef.current = turn;
      const assistantMsg: Message = { role: "assistant", content: "", pending: true, turn };
      setMessages((prev) => [...prev, assistantMsg]);

      // A new turn must never inherit a previous turn's leftover tail.
      revealBufferRef.current = "";
      // Analytics (#708): that a message went to the coach, and nothing of it: never its text, never its length.
      // Only a message the person sent: the page's own "New PR on: …" is not one (ruling R10).
      if (byPerson) track("coach_message_sent");
      await send(currentSessionId, userMsg.content, turn);
    },
    // setSessionId/setMessages/setFollowups are React state setters — always
    // referentially stable, same guarantee as if they came from useState
    // directly in this component. Listed here only because they're returned
    // from useCoachSession, so ESLint can't infer that stability through the
    // custom-hook indirection the way it does for useState calls.
    [sessionId, isStreaming, budgetNotice, shut, send, setSessionId, setMessages, setFollowups]
  );

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  // Land the caret in the composer once the page settles. No focus *restore*
  // on unmount any more: leaving the coach is a navigation, and the element
  // that was focused before it belongs to a page that no longer exists.
  useEffect(() => {
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 200);
    return () => clearTimeout(focusTimer);
  }, []);

  // Escape leaves the coach page. There is deliberately no Tab focus trap: a
  // trap belongs to a modal, and this is a page — trapping Tab here fights the
  // browser's own focus order for no benefit.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // A modal stacked on top of the page owns Escape. WorkoutLogModal is
      // mounted on this very route and closes on Escape too — without this
      // guard one press would both close the log AND navigate away, losing
      // the page behind it. It listens on `document` like we do and does not
      // stop propagation, so the check has to be here.
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // A workout saved from the log modal → celebrate any PRs in-chat.
  useEffect(() => {
    const handler = (e: Event) => {
      const prNames = (e as CustomEvent<{ prNames?: string[] }>).detail?.prNames ?? [];
      if (prNames.length === 0) return;
      void sendMessage(`New PR on: ${prNames.join(", ")} 🎉`, { byPerson: false });
    };
    window.addEventListener("coach:log-saved", handler);
    return () => window.removeEventListener("coach:log-saved", handler);
  }, [sendMessage]);

  // Puts a draft into the field unless the person has typed something there already.
  const keepDraft = useCallback((draft: string) => {
    setInput((current) => (current === "" ? draft : current));
  }, []);

  // The dock's draft, sent exactly once and only after the session id has
  // landed — sending earlier would POST a client-minted UUID the backend has
  // never seen, and the reply would land in a session nothing else reads.
  const initialSentRef = useRef(false);
  // Until the draft is handed to sendMessage, the page draws it itself (below, `draftPreview`); this
  // flips in the same tick as the send, so the real bubbles replace the drawn ones in one render.
  const [draftHandedOver, setDraftHandedOver] = useState(false);
  useEffect(() => {
    if (!initialMessage) return;
    if (initialSentRef.current) return;
    if (isLoadingSession || !sessionId) return;
    initialSentRef.current = true;
    // `sendMessage` pushes the user turn (setState) before POSTing it, which
    // trips react-hooks/set-state-in-effect. It is a real external-system sync
    // rather than derived state: the trigger is "the route handed us a draft
    // AND the session id has landed", which nothing but an effect can observe,
    // and initialSentRef makes it fire exactly once. Deferring it into a
    // microtask would hide the cascade, not remove it, and would make the
    // send race the very session load this effect waits for.
    //
    // A draft handed to a page that cannot send it (#399: a link from before the consent was missing, or
    // route state surviving a reload) is kept in the field, not sent and not lost: it waits for the
    // agreement. The same trigger and the same single call site, so the same reason holds.
    // The same hand-over as the send below, in its tick: the drawn draft goes as the real one arrives.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the once-only external sync explained above
    setDraftHandedOver(true);
    void (shut ? keepDraft(initialMessage) : sendMessage(initialMessage));
    onInitialMessageSent?.();
  }, [initialMessage, isLoadingSession, sessionId, sendMessage, keepDraft, onInitialMessageSent, shut]);

  // Stick to the bottom as the conversation grows (new message, thinking dots,
  // revealed reply) — unless the user has scrolled up to read history. This is
  // the natural chat behaviour of Gemini / Claude / ChatGPT.
  // `budgetNotice` is a dependency because the strip above the composer (#394)
  // takes its own height from this container when it appears, which would leave
  // the coach's message, the last thing in the list, half under it.
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    const c = messagesContainerRef.current;
    if (!c) return;
    // Two frames: let the reply / chips / dots finish laying out before we
    // measure scrollHeight. Instant (not smooth) so we land exactly at the
    // bottom even when content arrives in chunks (reply, then follow-up chips)
    // — a smooth animation targets a stale height and undershoots.
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        c.scrollTop = c.scrollHeight;
      })
    );
    return () => cancelAnimationFrame(id);
  }, [messages, followups, isLoadingSession, budgetNotice]);

  // Pinned means pinned through whatever changes the height: the coach's message
  // enters a beat after the thinking dots leave, and the strip above the composer
  // (#394) takes its own height from the container when it appears. Either leaves
  // the last bubble half under the composer when the scroll above ran first, and a
  // fixed delay racing an animation is a coin flip. So the two boxes that change
  // are watched, and while the view is stuck to the bottom it follows them. A user
  // who has scrolled up broke the stick (handleScrollIntent) and is not touched.
  // `ResizeObserver` is absent where there is no layout (jsdom): then the effects
  // above are all there is.
  useEffect(() => {
    const list = messagesListRef.current;
    const container = messagesContainerRef.current;
    if (!list || !container || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (stickToBottomRef.current && atBottomRef.current) {
        container.scrollTop = container.scrollHeight;
      }
    });
    observer.observe(list);
    observer.observe(container);
    return () => observer.disconnect();
  }, [isLoadingSession]);

  // Which follow-ups may show right now.
  //
  // The `messages.length > 1` gate exists to keep *suggestion* pills off the
  // screen before any conversation has happened — a bare opener with a rack of
  // questions under it reads as a menu, not a chat. That intent still holds for
  // the string pills, so they keep the gate.
  //
  // A `workout_log` pill is not a suggestion. It is the app's only trigger for
  // the workout log now that CoachBriefing is gone, and the backend emits it
  // precisely when there is a single assistant turn — exactly where the gate is
  // false. Applying the gate to it stranded the log entirely, so it does not.
  const conversationStarted = messages.length > 1;
  // The dock's message, drawn from the first frame until sendMessage takes it over (Apple design review
  // SH-1). Not on a shut page, which keeps the draft in the composer instead (#399).
  const draftPreview = initialMessage && !shut && !draftHandedOver ? initialMessage.trim() : null;
  const visibleFollowups = conversationStarted
    ? followups
    : followups.filter((f) => typeof f !== "string");

  const handleSend = () => {
    void sendMessage(input);
  };

  const handleStartNewChat = () => {
    // `stop()` (useCoachStream) then `startNewChat()` (useCoachSession) — same
    // two actions, same order as the pre-extraction `startNewChat`, which
    // opened with `if (isStreaming) stop();`. Split across two hooks to break
    // their circular dependency; see useCoachSession's module doc comment.
    if (isStreaming) stop();
    // No turn is the latest in the new chat: an older stream's follow-ups, still to come, are not for it.
    latestTurnRef.current = null;
    void startNewChat().finally(() => {
      setTimeout(() => inputRef.current?.focus(), 150);
    });
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div
      className="relative flex h-full min-h-0 w-full flex-col overflow-hidden"
      style={{ color: CREAM, fontFamily: "var(--font-body)" }}
    >
      {/* Page header row. Not a floating pill and not the old identity band:
          the same eyebrow-plus-controls vocabulary every other page uses,
          aligned to the message column so the eyebrow sits above the first
          avatar rather than over the gradient. */}
      <div className="mx-auto flex w-full max-w-[46rem] shrink-0 items-center justify-between gap-3 pt-2 pb-1">
        {/* No visible page title by design — the eyebrow below carries the
            date, not the page name. Every other view in the app names itself
            in an h1; this one does it for assistive technology only. Mirrors
            journey/index.tsx. */}
        <h1 className="sr-only">Coach</h1>
        <span
          className="text-[10px] uppercase"
          style={{
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.32em",
            color: CREAM_DIM,
          }}
        >
          Coach · {today}
        </span>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleStartNewChat}
            disabled={isLoadingSession}
            aria-label="New chat"
            className={`flex h-9 items-center gap-1.5 rounded-[10px] px-3 text-[11px] uppercase transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-(--accent-amber) disabled:cursor-not-allowed disabled:opacity-40 active:scale-[0.96] ${HIT_AREA_TALLER}`}
            style={{
              color: CREAM_SOFT,
              background: "transparent",
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.08em",
            }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.background = TOKENS.creamHairline;
                e.currentTarget.style.color = CREAM;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = CREAM_SOFT;
            }}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Back to dashboard"
            className={`flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-(--accent-amber) active:scale-[0.94] ${HIT_AREA_FROM_36_LEFTWARD}`}
            style={{ color: CREAM_SOFT, background: "transparent" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = TOKENS.creamHairline;
              e.currentTarget.style.color = CREAM;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = CREAM_SOFT;
            }}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      {sessionEphemeral && (
        <div
          role="status"
          className="mx-auto mt-2 w-full max-w-[46rem] shrink-0 rounded-lg px-4 py-3 text-xs leading-relaxed"
          style={{
            background: TERRACOTTA_SOFT,
            color: CREAM,
            borderLeft: `2px solid ${TERRACOTTA}`,
            fontFamily: "var(--font-body)",
          }}
        >
          <strong style={{ color: TERRACOTTA, fontWeight: 600 }}>History not persisting.</strong>{" "}
          Redis is offline. Messages will be lost on reload.
        </div>
      )}

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        onWheel={handleScrollIntent}
        onTouchMove={handleScrollIntent}
        className="coach-scroll relative min-h-0 flex-1 overflow-y-auto overscroll-contain pt-5 pb-6"
      >
        {isLoadingSession && draftPreview ? (
          // The dock's message, shown the moment the page opens (Apple design review SH-1). It is sent
          // only once the session id has landed (the effect above says why), which is one round trip,
          // two for an empty session; the person used to watch their words vanish into a spinner for
          // that long. These are the same two bubbles sendMessage pushes, and the list below keeps
          // drawing them after the session lands until the send takes over, so they never blink out.
          <div className="mx-auto flex w-full max-w-[46rem] flex-col gap-6">
            <ul className="m-0 flex list-none flex-col gap-6 p-0" aria-label="Sending your message">
              <MessageBubble message={{ role: "user", content: draftPreview }} />
              <MessageBubble message={{ role: "assistant", content: "", pending: true }} />
            </ul>
          </div>
        ) : isLoadingSession ? (
          // A status region carrying its own words, the way `PanelSkeleton`
          // does: the spinner is decoration, and under prefers-reduced-motion
          // it stops (fonts.css names `.animate-spin`), so the words are all
          // that is left of the wait (#221).
          <div className="flex h-full items-center justify-center" role="status" aria-label="Loading conversation">
            <Loader2 className="h-5 w-5 animate-spin" style={{ color: AMBER }} aria-hidden />
            <span className="sr-only">Loading conversation</span>
          </div>
        ) : (
          <div ref={messagesListRef} className="mx-auto flex w-full max-w-[46rem] flex-col gap-6">
            <ul
              id={liveRegionId}
              aria-live="polite"
              aria-relevant="additions text"
              aria-atomic="false"
              className="m-0 flex list-none flex-col gap-6 p-0"
            >
              {messages.map((message, index) => {
                // "Got it" belongs to a trigger's opener while it is the whole
                // transcript (#269); once the user replies it is part of the
                // conversation, and dismissing it would rewrite history.
                const trigger = message.openerTrigger;
                const dismissable =
                  trigger != null && messages.length === 1 && !isStreaming;
                return (
                  <MessageBubble
                    key={index}
                    message={message}
                    onDismiss={
                      dismissable ? () => dismissOpener(trigger.occurrenceId) : undefined
                    }
                    onAnswer={
                      dismissable
                        ? (answer) => answerProposal(trigger.occurrenceId, answer)
                        : undefined
                    }
                    onAnswerOffer={
                      dismissable && message.openerOffer
                        ? (answer) =>
                            answerOffer(trigger.occurrenceId, answer, message.openerOffer?.kind)
                        : undefined
                    }
                  />
                );
              })}
              {/* The dock's message between the session landing and the send taking it over (one effect,
                  after a paint): drawn here so it never blinks out of the list in that gap. */}
              {draftPreview && (
                <>
                  <MessageBubble key="draft-user" message={{ role: "user", content: draftPreview }} />
                  <MessageBubble key="draft-thinking" message={{ role: "assistant", content: "", pending: true }} />
                </>
              )}
            </ul>

            {/* Follow-ups after an AI reply. V3 moves them to the user's side:
                they are drafts of your next message, not coach content, so
                they hang off the user's avatar column (pr-12 = the 48px the
                36px avatar plus the 12px row gap occupy) under their own
                eyebrow. */}
            {visibleFollowups.length > 0 && !isStreaming && (
              <div className="flex flex-col items-end gap-2 pr-12">
                <span
                  className="text-[9px] uppercase"
                  style={{
                    fontFamily: "var(--font-mono)",
                    letterSpacing: "0.24em",
                    color: TOKENS.creamDim,
                  }}
                >
                  Ask next
                </span>
                <div
                  role="group"
                  aria-label="Suggested replies"
                  className="flex flex-wrap justify-end gap-2"
                >
                  {/* A follow-up is either a question to send or a
                      WorkoutLogPill that opens the log modal. The previous
                      `.filter(typeof === "string")` dropped every pill of the
                      second kind on the floor — and since CoachBriefing (which
                      owned the other `coach:open-log` trigger) is gone, this is
                      now the only way to open the log. */}
                  {visibleFollowups.map((f, i) =>
                    typeof f === "string" ? (
                      <CoachPill key={`q:${f}`} index={i} onClick={() => void sendMessage(f)}>
                        {f}
                      </CoachPill>
                    ) : (
                      <CoachPill
                        key={`log:${f.label}`}
                        index={i}
                        onClick={() => window.dispatchEvent(new CustomEvent("coach:open-log"))}
                      >
                        {f.label}
                      </CoachPill>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Scroll-to-bottom affordance */}
        <AnimatePresence>
          {showScrollDown && (
            <motion.button
              type="button"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.18 }}
              onClick={scrollToBottom}
              aria-label="Scroll to latest message"
              // Outline, not a ring: boxShadow is set inline below, and an
              // inline declaration beats the class Tailwind's ring rides on.
              className="sticky bottom-2 ml-auto mr-1 flex h-10 w-10 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--accent-amber)"
              style={{
                background: WARM_DARK,
                border: `1px solid ${AMBER_BORDER}`,
                color: AMBER,
                boxShadow: "0 8px 20px -8px rgba(20, 14, 12, 0.55)",
              }}
            >
              <ArrowDown className="h-4 w-4" aria-hidden />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Composer — the same bar as CoachDock, so arriving from the dock feels
          like the bar you typed in simply moved up the page. */}
      <Composer
        value={input}
        onChange={setInput}
        onSubmit={handleSend}
        onStop={stop}
        isStreaming={isStreaming}
        disabled={isLoadingSession}
        placeholder="Message the coach…"
        inputRef={inputRef}
        lock={budgetNotice ? { id: budgetStripId, text: budgetNotice.strip } : null}
        shut={
          shut
            ? {
                id: consentStripId,
                text: COPY.aiOffLine,
                // The way back is Settings › Privacy (CO-D3): the strip informs, it never asks in place.
                action: <SettingsPrivacyLink />,
              }
            : null
        }
      />
    </div>
  );
}
